import type { LevelView } from '@bioforge/protocol';
import { Application } from 'pixi.js';
import { ApiClient, ApiRequestError, browserTokenStore } from './api/api-client.js';
import { ConnectScene } from './connect-scene.js';
import { EncyclopediaScene } from './encyclopedia-scene.js';
import { ShopScene } from './shop-scene.js';
import { ExpeditionScene } from './expedition-scene.js';
import { loadout } from './labels.js';
import { MenuScene } from './menu-scene.js';
import { NetScene, loadSession } from './net-scene.js';
import { NetClient } from './net/net-client.js';
import { ConnectionIndicator } from './overlays/connection-indicator.js';
import { reducedMotion } from './overlays/html.js';
import { StoryOverlay, type StoryOptions } from './overlays/story-overlay.js';
import type { AppContext, Navigator, OnlineIntent, Scene } from './scene.js';
import { SoloScene } from './solo-scene.js';
import { afterVictory, beforeLevel, seenTracker } from './story/story.js';
import { isPortrait } from './ui/frame.js';
import { setReducedMotion } from './ui/motion.js';
import { loadArt } from './ui/textures.js';
import { loadFonts } from './ui/theme.js';

/** Адрес сервера: VITE_SERVER_URL или тот же хост на порту 3000. */
function serverUrl(): string {
  const configured = import.meta.env['VITE_SERVER_URL'] as string | undefined;
  return configured ?? `${window.location.protocol}//${window.location.hostname}:3000`;
}

/**
 * Переключает экраны внутри приложения. Адрес страницы ничем не управляет: единственное
 * исключение — ссылка-приглашение `?room=КОД`, которая сразу убирается из адреса.
 */
class Game implements Navigator {
  private scene: Scene | null = null;
  private story: StoryOverlay | null = null;
  private ctx: AppContext | null = null;
  private readonly indicator = new ConnectionIndicator();
  private readonly api = new ApiClient({
    baseUrl: serverUrl(),
    tokens: browserTokenStore(),
    onConnection: (state) => this.indicator.set(state),
  });

  /**
   * Как заново открыть текущий экран при повороте телефона. Только у экранов без состояния
   * игры (меню, экспедиция, энциклопедия, магазин); бой и сетевой экран перестраиваются сами.
   */
  private reopen: (() => void) | null = null;
  private portrait = isPortrait();

  constructor(private readonly app: Application) {
    window.addEventListener('resize', () => {
      if (isPortrait() === this.portrait) return;
      this.portrait = isPortrait();
      this.reopen?.();
    });
  }

  private show(scene: Scene, reopen: (() => void) | null = null): void {
    this.story?.destroy();
    this.story = null;
    this.scene?.destroy();
    this.scene = scene;
    this.reopen = reopen;
    this.app.stage.addChild(scene.view);
  }

  private get context(): AppContext {
    if (!this.ctx) throw new Error('Данные с сервера ещё не загружены');
    return this.ctx;
  }

  menu(notice?: string): void {
    this.show(new MenuScene(this, this.context, notice), () => this.menu());
  }

  expedition(notice?: string): void {
    this.show(new ExpeditionScene(this, this.context, this.app.ticker, notice), () =>
      this.expedition(),
    );
  }

  encyclopedia(): void {
    const ctx = this.context;
    this.show(
      new EncyclopediaScene({
        ctx,
        reducedMotion: reducedMotion(ctx.settings.accessibility.reducedMotion),
        onClose: () => this.menu(),
      }),
      () => this.encyclopedia(),
    );
  }

  shop(): void {
    this.show(new ShopScene(this, this.context), () => this.shop());
  }

  play(level: LevelView): void {
    void this.introduce(level).then(() => this.startAttempt(level));
  }

  /**
   * Перед уровнем (SPEC.md, 15.7 и 19.3): новая сцена (у босса — вступление), затем карточки
   * всего нового. Встреченное отмечается, даже если игрок пропустил показ или выключил подсказки.
   */
  private async introduce(level: LevelView): Promise<void> {
    const ctx = this.context;
    const { scene, cards } = beforeLevel(level, ctx.content, ctx.seen);
    await this.tell({
      scene,
      cards: ctx.settings.accessibility.hints ? cards : [],
    });
    if (scene) void ctx.seen.mark('scene', [scene.id]);
    void ctx.seen.mark(
      'intro',
      cards.map((card) => card.id),
    );
  }

  celebrate(level: LevelView): void {
    const ctx = this.context;
    const scene = afterVictory(level, ctx.content, ctx.seen);
    if (!scene) return;
    void this.tell({ scene }).then(() => ctx.seen.mark('scene', [scene.id]));
  }

  /** Показывает сцену и карточки поверх текущего экрана; смена экрана закрывает показ. */
  private tell(story: Pick<StoryOptions, 'scene' | 'cards'>): Promise<void> {
    const ctx = this.context;
    this.story?.destroy();
    const overlay = new StoryOverlay({
      ...story,
      content: ctx.content,
      reducedMotion: reducedMotion(ctx.settings.accessibility.reducedMotion),
    });
    this.story = overlay;
    return overlay.done.then(() => {
      if (this.story === overlay) this.story = null;
    });
  }

  private startAttempt(level: LevelView): void {
    const ctx = this.context;
    const offered = level.abilities;
    const ability = offered.includes(loadout.solo) ? loadout.solo : offered[0];
    ctx.api.createAttempt({ levelId: level.id, ...(ability ? { ability } : {}) }).then(
      (attempt) => this.show(new SoloScene(this.app, this, ctx, level, attempt)),
      (error: unknown) => {
        const locked = error instanceof ApiRequestError && error.code === 'level-locked';
        this.expedition(
          locked
            ? 'Уровень закрыт: сначала пройдите предыдущий.'
            : 'Сервер недоступен. Попробуйте ещё раз.',
        );
      },
    );
  }

  online(intent: OnlineIntent): void {
    this.show(new NetScene(this, new NetClient(serverUrl()), intent, this.context));
  }

  /** Первый экран: загрузка аккаунта и контента; без сервера — «Нет соединения» с повтором. */
  start(): void {
    const connect = new ConnectScene(() => void this.load(connect));
    this.show(connect);
    void this.load(connect);
  }

  private async load(connect: ConnectScene): Promise<void> {
    connect.connecting();
    try {
      const account = await this.api.ensureAccount();
      const [content, progress, profile, shop] = await Promise.all([
        this.api.content(),
        this.api.progress(),
        this.api.profile(),
        this.api.shop(),
      ]);
      this.ctx = {
        api: this.api,
        connection: this.indicator,
        account,
        content,
        progress,
        seen: seenTracker(this.api, profile.seen),
        settings: profile.settings,
        shop,
      };
      setReducedMotion(reducedMotion(profile.settings.accessibility.reducedMotion));
    } catch {
      connect.failed(navigator.onLine === false);
      return;
    }
    this.enter();
  }

  /** После загрузки: приглашение в комнату, возврат в свою комнату после перезагрузки или меню. */
  private enter(): void {
    const invite = new URLSearchParams(window.location.search).get('room');
    if (invite !== null) {
      window.history.replaceState(null, '', window.location.pathname);
      this.online({ kind: 'join', code: invite.trim().toUpperCase() });
      return;
    }
    const session = loadSession();
    if (session) {
      this.online({ kind: 'join', code: session.roomCode });
      return;
    }
    this.menu();
  }
}

async function bootstrap(): Promise<void> {
  const app = new Application();
  await app.init({
    background: 0x070a1c,
    resizeTo: window,
    antialias: true,
    // Чёткий текст и рисунки на экранах высокой плотности.
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  document.body.appendChild(app.canvas);
  await Promise.all([loadFonts(), loadArt()]);
  new Game(app).start();
}

void bootstrap();
